import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { RentalOverdueRow, RentalOverdueStatusService } from 'app/services/rental-overdue-status.service';

/**
 * Toolbar BELL for overdue rentals (WS-R; the kdc_registry notification-bell
 * look on the booking-queue-indicator plumbing). Badge = count of rental
 * lines out past their return date + the temple's grace days; clicking opens
 * a panel listing them (devotee, phone, item, days overdue) with a jump to
 * the Rental Enquiry. Rendered ONLY for users holding a rental/common role —
 * the endpoints are rentalBillingAccess-gated server-side too.
 */
@Component({
    selector: 'app-rental-overdue-bell',
    templateUrl: './rental-overdue-bell.component.html',
    styleUrls: ['./rental-overdue-bell.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RentalOverdueBellComponent implements OnInit, OnDestroy {

    private readonly status = inject(RentalOverdueStatusService);
    private readonly router = inject(Router);

    readonly open = signal(false);
    readonly rows = signal<RentalOverdueRow[]>([]);
    readonly loadingRows = signal(false);

    readonly count = this.status.count;

    private static readonly RENTAL_ROLES = [
        'RENTAL_BILLING_ACCESS', 'RENTAL_FULL_ACCESS',
        'FULL_ACCESS', 'COMPANY_ACCESS', 'TEMPLE_ADMIN', 'AUDITOR_ACCESS', 'REPORT_FULL_ACCESS',
    ];
    /** Bell renders (and polls) only for rental-capable users. */
    readonly visible: boolean = (() => {
        try {
            const user = JSON.parse(sessionStorage.getItem('CURRENT_USER') || '{}');
            return ((user.roles || []) as { name: string }[])
                .some(r => RentalOverdueBellComponent.RENTAL_ROLES.includes(r.name));
        } catch {
            return false;
        }
    })();

    ngOnInit() {
        if (this.visible) {
            this.status.startPolling();
        }
    }

    ngOnDestroy() {
        if (this.visible) {
            this.status.stopPolling();
        }
    }

    toggle() {
        this.open.update(v => !v);
        if (this.open()) {
            this.status.refresh();
            this.loadRows();
        }
    }

    close() {
        this.open.set(false);
    }

    loadRows() {
        this.loadingRows.set(true);
        this.status.rows().subscribe({
            next: (resp) => {
                this.rows.set(resp?.result || []);
                this.loadingRows.set(false);
            },
            error: () => {
                this.rows.set([]);
                this.loadingRows.set(false);
            }
        });
    }

    openEnquiry() {
        this.close();
        this.router.navigate(['/app/rental-v2/enquiry']);
    }
}
